import {inject, Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AppState, RouterStateUrl, selectList,} from '../../core.state';

import {Store} from '@ngrx/store';
import {catchError, debounceTime, exhaustMap, filter, map, switchMap, takeUntil, withLatestFrom} from "rxjs/operators";

import {of, Subject} from "rxjs";
import {SyndicateClient} from "../../../client/syndicate";

import {
  callHighlighted,
  callList, callSyndicateWins,
  failSyndicateList, failTopSquadsToJoin,
  listLoadMore,
  loadList,
  successList, successSyndicateWins, successTopSquadsToJoin, getTopSquadsToJoin
} from "../syndicates.actions";
import {selectFilters} from "../syndicates.selectors";
import {uniq} from 'lodash-es';
import {routerNavigationAction} from '@ngrx/router-store';
import {CalculationsService} from "../../../services/utils/calculations/calculations.service";
import {Config, getSoftCheckState, SyndicatesScope} from "@cbcore/index";

const HIGHLIGHTS_MODEL = {
  filters: {}
};
const ROI_LIMIT = 1000;

@Injectable()
export class SyndicatesListEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  routerNavigationSyndicatesList = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter((routerProps) => (routerProps.payload.event.url.includes('/syndicates/join') && !routerProps.payload.event.url.includes('/syndicates/join/pool'))),
      takeUntil(this.destroy$),
      exhaustMap((routerProps) => {
        return of(loadList({
          model: {
            pool_id: (<any>routerProps.payload.routerState).queryParams.pool_id,
            search: (<any>routerProps.payload.routerState).queryParams.search
          }
        }));
      }))
  }, {dispatch: true});

  loadList = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadList),
      withLatestFrom(this.store.select(selectFilters)),
      takeUntil(this.destroy$),
      switchMap(([propVal, list_pagination]) => of(callList({model: {filters: list_pagination.filters}}))
      )
    )
  }, {dispatch: true});

  getTopSquadsToJoin = createEffect(() => {
    return this.actions$.pipe(
      ofType(getTopSquadsToJoin),
      withLatestFrom(this.store.select(getSoftCheckState)),
      debounceTime(300),
      // Use switchMap here - user search should cancel previous requests
      switchMap(([propVal, us_state]) =>
        this.syndicatesClient.topSquadsToJoin().pipe(
          map(data => {
            const safeData = data ?? { items: [] } as SyndicatesScope.SyndicateList;
            let filteredSyndicates: SyndicatesScope.SyndicateList = safeData;
            try {
              const result = this.filterSyndicateByState(safeData, us_state);
              filteredSyndicates = {
                ...result,
                items: (result?.items ?? []).slice(0, 3),
              };
            } catch (e: any) {
              filteredSyndicates = {
                ...safeData,
                items: (safeData.items ?? []).slice(0, 3),
              };
            }
            return successTopSquadsToJoin({list: filteredSyndicates})
          }),
          catchError(error => {
            console.error('[Syndicates List Effects] Failed to load top squads:', error);
            return of(failTopSquadsToJoin({error: error}));
          })
        )
      )
    )
  }, {dispatch: true})

  /**
   * Filtering by pool_type. if it doesn't exist don't make stupid API requests
   */
  callList = createEffect(() => {
    return this.actions$.pipe(
      ofType(callList),
      withLatestFrom(this.store.select(selectFilters), this.store.select(selectList), this.store.select(getSoftCheckState)),
      filter(([propVal, filters, list]) => {
        const hasPoolId = 'pool_id' in (filters?.filters || {});
        const hasPoolTypes = 'pool_types' in (filters?.filters || {});
        return hasPoolId || hasPoolTypes;
      }),
      switchMap(([propVal, filters, list, us_state]) => {
        return this.syndicatesClient.list(filters).pipe(
          map(data => {
            let filteredSyndicates = data;
            try {
              filteredSyndicates = this.filterSyndicateByState(data, us_state);
            } catch(e: any) {
              //
            }
            let have_items = true;
            // for pagination on syndicates
            if (filteredSyndicates.items.length === 0 || filteredSyndicates.items.length < (filters.per ?? Infinity)) {
              have_items = false;
            }
            if (filters.page === 1) {
              const combinedList = {items: filteredSyndicates.items, have_items: have_items};
              return successList({response: combinedList});
            } else {
              // Pre-existing assumption: list is already populated on a "load more" (page !== 1) fetch — not independently verified
              const combinedList = {items: uniq(list!.items.concat(filteredSyndicates.items)), have_items: have_items};
              return successList({response: combinedList});
            }
          }),
          catchError(error => {
            console.error('[Syndicates List Effects] Failed to load syndicate list:', error);
            return of(failSyndicateList({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  listLoadMore = createEffect(() => {
    return this.actions$.pipe(
      ofType(listLoadMore),
      withLatestFrom(this.store.select(selectFilters)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, filters]) => of(callList({model: filters as any})))
    )
  }, {dispatch: true});

  initWinsList = createEffect(() => {
    return this.actions$.pipe(
      ofType(callHighlighted),
      // Use exhaustMap to prevent duplicate concurrent requests
      exhaustMap(() => {
        return this.syndicatesClient.highlighted().pipe(
          map((data: any) => {
            HIGHLIGHTS_MODEL.filters = {
              filters: {
                status: 'historic',
                syndicate_ids: data.syndicate_ids
              }
            }
            return callSyndicateWins({model: HIGHLIGHTS_MODEL.filters});
          }),
          catchError(error => {
            console.error('[Syndicates List Effects] Failed to load highlighted syndicates:', error);
            return of(failSyndicateList({error: error}));
          })
        )
      })
    )
  });

  syndicateWins = createEffect(() => {
    return this.actions$.pipe(
      ofType(callSyndicateWins),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate concurrent requests
      exhaustMap((propVal: any) => {
        return this.syndicatesClient.listSyndicateWins(propVal.model).pipe(
          map((data: any) => {
            const parsedData = this.calculationService.uiSWSortItems(data.items, propVal.model, ROI_LIMIT);
            return successSyndicateWins({response: parsedData});
          }),
          catchError(error => {
            console.error('[Syndicates List Effects] Failed to load syndicate wins:', error);
            return of(failSyndicateList({error: error}));
          })
        )
      })
    )
  });

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public calculationService: CalculationsService,
    public syndicatesClient: SyndicateClient
  ) {
  }

  private _config = inject(Config);

  private filterSyndicateByState(syndicateData: any, us_state: string | undefined): any {
    if (!us_state) {
      console.warn('No us_state available when filtering syndicates');
      return syndicateData;
    }

    // If syndicateData doesn't have items array, return as-is
    if (!syndicateData?.items || !Array.isArray(syndicateData.items)) {
      console.warn('Syndicate data does not contain items array');
      return syndicateData;
    }

    // Get the allowed states from environment
    const allowedStates = this._config.CONFIG.filteredPoolsStates || [];
    const filteredPoolCode = this._config.CONFIG.filteredPoolRuleCode;

    if (allowedStates.length === 0 || !filteredPoolCode) {
      console.warn('No filtering as no states or name_code set');
      return syndicateData;
    }

    // Trim whitespace from us_state
    const trimmedState = us_state.trim();
    // Filter the items array based on the pool's name_code
    const filteredItems = syndicateData.items.filter((syndicate: any) => {
      // If pool's name_code is 'FILTERED' and user state is in allowed states, exclude it
      if (syndicate.pool.name_code === filteredPoolCode && allowedStates.includes(trimmedState)) {
        console.log('Filtering out syndicate (id:', syndicate.id, ') with pool (id:', syndicate.pool.id, ') - state', trimmedState, 'in not allowed states');
        return false;
      }

      // Keep all other syndicates
      return true;
    });
    // Return the syndicate data with filtered items
    return {
      ...syndicateData,
      items: filteredItems,
      total_items: syndicateData.total_items !== null
        ? syndicateData.total_items - (syndicateData.items.length - filteredItems.length)
        : null
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
